/**
 * Reading campaigns, and asking the gate to change one.
 *
 * Hand-rolled `fetch` against PostgREST (PLAN §2). A URL, a header and
 * a JSON body — it does not earn a second dependency, and the query strings
 * below are more legible than the builder that would generate them.
 *
 * **Everything here is a read except the three that go through `campaign`.**
 * There is no insert or update policy on either table, so a write attempted
 * from this file would be refused by the database rather than by a convention
 * — which is the arrangement §8.3 asks for.
 */

import { FUNCTIONS_URL, REST_URL, SUPABASE_ANON_KEY } from './config.ts';
import { accessToken } from './session.ts';

export interface CampaignRow {
  readonly id: number;
  readonly template: string;
  readonly sender: string;
  readonly status: string;
  readonly recipient_count: number;
  readonly estimated_parts: number;
  readonly accepted_count: number;
  readonly delivered_count: number;
  readonly failed_count: number;
  readonly actual_parts: number | null;
  readonly created_at: string;
  readonly started_at: string | null;
  readonly completed_at: string | null;
  readonly fatal_error: string | null;
  readonly fatal_error_key: string | null;
}

export interface RecipientRow {
  readonly id: number;
  readonly line: number;
  readonly phone: string;
  readonly promo_code: string;
  readonly final_message: string;
  readonly estimated_parts: number;
  readonly actual_parts: number | null;
  readonly status: string;
  readonly delivery_status: string | null;
  readonly error_key: string | null;
  readonly error_message: string | null;
  readonly sent_at: string | null;
  readonly delivered_at: string | null;
}

/** The session ended between the page loading and this request. */
export class NotSignedIn extends Error {
  constructor() {
    super('not signed in');
    this.name = 'NotSignedIn';
  }
}

async function headers(): Promise<Record<string, string>> {
  const token = await accessToken();
  if (token === undefined) throw new NotSignedIn();

  return {
    apikey: SUPABASE_ANON_KEY,
    authorization: `Bearer ${token}`,
    'content-type': 'application/json',
  };
}

async function read<T>(path: string): Promise<T> {
  const response = await fetch(`${REST_URL}${path}`, { headers: await headers() });
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) throw new Error(`PostgREST answered ${response.status}`);

  return (await response.json()) as T;
}

const CAMPAIGN_FIELDS =
  'id,template,sender,status,recipient_count,estimated_parts,accepted_count,delivered_count,' +
  'failed_count,actual_parts,created_at,started_at,completed_at,fatal_error,fatal_error_key';

/** Most recent first, which is the only order a history is ever read in. */
export function campaigns(limit = 50): Promise<readonly CampaignRow[]> {
  return read(`/campaigns?select=${CAMPAIGN_FIELDS}&order=created_at.desc&limit=${limit}`);
}

export async function campaign(id: number): Promise<CampaignRow | undefined> {
  const rows = await read<CampaignRow[]>(`/campaigns?id=eq.${id}&select=${CAMPAIGN_FIELDS}&limit=1`);
  return rows[0];
}

export interface RecipientPage {
  readonly rows: readonly RecipientRow[];
  /** How many there are in total, so the screen can say "showing 100 of 10,000". */
  readonly total: number;
}

/**
 * One page of recipients.
 *
 * Paged rather than whole: ten thousand rows is a few megabytes of JSON and a
 * table no browser lays out quickly, and nobody reads past the first screen
 * except to find one number — which is what the filter is for.
 */
export async function recipients(
  campaignId: number,
  options: {
    readonly offset?: number;
    readonly limit?: number;
    readonly status?: string;
    /** Matched against the number and the promo code, case-insensitively. */
    readonly search?: string;
  } = {},
): Promise<RecipientPage> {
  const limit = options.limit ?? 100;
  const offset = options.offset ?? 0;
  const filter = options.status === undefined || options.status === '' ? '' : `&status=eq.${options.status}`;
  const search = searchFilter(options.search);

  const response = await fetch(
    `${REST_URL}/recipients?campaign_id=eq.${campaignId}${filter}${search}` +
      `&select=id,line,phone,promo_code,final_message,estimated_parts,actual_parts,status,` +
      `delivery_status,error_key,error_message,sent_at,delivered_at` +
      `&order=line&offset=${offset}&limit=${limit}`,
    // `count=exact` costs a second scan and buys the only number that makes
    // paging comprehensible.
    { headers: { ...(await headers()), prefer: 'count=exact' } },
  );
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) throw new Error(`PostgREST answered ${response.status}`);

  return { rows: (await response.json()) as RecipientRow[], total: rangeTotal(response) };
}

/**
 * One box, two columns: a number or a promo code, whichever was typed.
 *
 * PostgREST's `or=(…)` takes a bare comma-separated list, so anything in the
 * query that a comma or a parenthesis could terminate has to go — otherwise a
 * search for `(` is a 400 and a search for `,` silently becomes two conditions.
 * Stripped rather than escaped: there is no escape for these inside `or=`, and
 * no promo code contains them.
 *
 * `*` is the wildcard `ilike` wants; a literal `%` typed by a person would be
 * one too, so it is removed for the same reason.
 */
function searchFilter(query: string | undefined): string {
  const cleaned = (query ?? '').trim().replace(/[(),*%\\]/g, '');
  if (cleaned === '') return '';

  const term = encodeURIComponent(`*${cleaned}*`);
  return `&or=(phone.ilike.${term},promo_code.ilike.${term})`;
}

/** `content-range: 0-99/10000` — the second half is what was asked for. */
function rangeTotal(response: Response): number {
  const total = Number(response.headers.get('content-range')?.split('/')[1] ?? '');
  return Number.isFinite(total) ? total : 0;
}

export interface Distribution {
  readonly parts: number;
  readonly recipients: number;
}

/**
 * How many recipients get a one-SMS message, how many get two.
 *
 * The honest answer to "what does this cost" is not one number: promo codes
 * vary in length, so a template can be one segment for most recipients and two
 * for a handful, and a screen that quoted only the total would be quoting the
 * wrong thing to whoever is about to approve it.
 *
 * A function rather than a query because PostgREST has no grouping, and
 * counting ten thousand values in the browser is a megabyte of JSON to produce
 * three rows.
 */
export async function distribution(campaignId: number): Promise<readonly Distribution[]> {
  const response = await fetch(`${REST_URL}/rpc/campaign_distribution`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({ p_campaign_id: campaignId }),
  });
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) throw new Error(`campaign_distribution answered ${response.status}`);

  return (await response.json()) as readonly Distribution[];
}

// ---------------------------------------------------------------------------
// Changing something

export interface GateFailure {
  readonly error: string;
  readonly problems?: readonly { readonly name: string; readonly params: Record<string, number> }[];
  readonly [key: string]: unknown;
}

/**
 * Calls the `campaign` Edge Function, which is the only door to validation.
 *
 * The browser reaches the verdict too — that is what keeps Continue accurate
 * while a screen is being filled in — but this is the one that counts, because
 * the browser's copy can be skipped by turning off script or by posting by hand.
 */
export async function gate(body: Readonly<Record<string, unknown>>): Promise<unknown> {
  const response = await fetch(`${FUNCTIONS_URL}/campaign`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (response.status === 401) throw new NotSignedIn();

  const answer = (await response.json()) as Record<string, unknown>;
  if (!response.ok) throw new GateRefused(answer as GateFailure);

  return answer;
}

export class GateRefused extends Error {
  readonly failure: GateFailure;

  constructor(failure: GateFailure) {
    super(failure.error);
    this.name = 'GateRefused';
    this.failure = failure;
  }
}

/**
 * Stop, Resume, Retry and Refresh go straight to their RPCs rather than through
 * the gate: each holds its own guarantee — three of them an atomic
 * `update … where status = …`, and Refresh a pure read that cannot send
 * anything — and there is nothing about them for validation to have an opinion
 * on. The gate exists for the two things that need `checkCampaign`.
 */
export async function control(
  action: 'cancel_sending' | 'resume_sending' | 'retry_failed' | 'refresh_delivery',
  campaignId: number,
): Promise<unknown> {
  const response = await fetch(`${REST_URL}/rpc/${action}`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({ p_campaign_id: campaignId }),
  });
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) throw new Error(`${action} answered ${response.status}`);

  return await response.json();
}

// ---------------------------------------------------------------------------
// What the provider and the pipeline say about themselves

export interface Health {
  readonly pipeline: {
    readonly running: boolean;
    readonly watchdog: boolean;
    readonly queued: number;
    readonly sending: number;
  } | null;
  readonly account:
    | { readonly ok: true; readonly state: string; readonly account: string; readonly smsPrice: string }
    | { readonly ok: false }
    | null;
}

/**
 * One request for both halves, and either may come back `null`.
 *
 * Through the gate rather than straight to PostgREST because half of it is the
 * provider's balance, and the provider is only reachable from a function that
 * holds its password. The pipeline half could have been an RPC on its own; two
 * requests to draw one strip is two chances to render half of it.
 */
export function health(): Promise<Health> {
  return gate({ action: 'health' }) as Promise<Health>;
}

export interface LookupResult {
  readonly phone: string;
  readonly outcome: 'known' | 'unknown' | 'malformed' | 'refused' | 'unavailable';
  readonly operator?: string;
  readonly region?: string;
  readonly messageKey: string;
}

/**
 * Asks the operator about a batch of numbers. Free at the provider, so the only
 * budget is time.
 *
 * `complete` is false when the provider stopped answering part-way: whatever
 * came back is real, and the caller is told the rest is missing rather than
 * being handed a short list as though it were the whole one.
 */
export function lookup(phones: readonly string[]): Promise<{ results: readonly LookupResult[]; complete: boolean }> {
  return gate({ action: 'lookup', phones }) as Promise<{ results: readonly LookupResult[]; complete: boolean }>;
}

// ---------------------------------------------------------------------------
// Saved texts

export interface TemplateRow {
  readonly id: number;
  readonly name: string;
  readonly body: string;
  readonly updated_at: string;
}

export function templates(): Promise<readonly TemplateRow[]> {
  return read('/templates?select=id,name,body,updated_at&order=name');
}

export function saveTemplate(name: string, body: string): Promise<unknown> {
  return rpc('save_template', { p_name: name, p_body: body });
}

export function deleteTemplate(id: number): Promise<unknown> {
  return rpc('delete_template', { p_id: id });
}

// ---------------------------------------------------------------------------
// Who has heard from us lately, and who sent what

export interface RecentContact {
  readonly phone: string;
  readonly last_sent_at: string;
  readonly campaign_id: number;
}

/**
 * Which of these numbers already received something, and when.
 *
 * Asked about the numbers on the screen rather than answered from the whole
 * history, so the reply is at most as long as the list being sent and usually
 * empty.
 */
export function recentContacts(phones: readonly string[], days: number): Promise<readonly RecentContact[]> {
  return rpc('recent_contacts', { p_phones: phones, p_days: days }) as Promise<readonly RecentContact[]>;
}

export interface CampaignAuthor {
  readonly campaign_id: number;
  readonly email: string;
}

export function campaignAuthors(ids: readonly number[]): Promise<readonly CampaignAuthor[]> {
  return rpc('campaign_authors', { p_campaign_ids: ids }) as Promise<readonly CampaignAuthor[]>;
}

/** One RPC, with the session's token. Throws on anything but a 2xx. */
async function rpc(name: string, args: Readonly<Record<string, unknown>>): Promise<unknown> {
  const response = await fetch(`${REST_URL}/rpc/${name}`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(args),
  });
  if (response.status === 401) throw new NotSignedIn();
  if (!response.ok) throw new Error(`${name} answered ${response.status}`);

  const text = await response.text();
  return text.trim() === '' ? null : JSON.parse(text);
}

/**
 * Just the numbers on a campaign, for the two checks that need the list itself.
 *
 * `select=phone` and nothing else: the fatigue check and the operator lookup
 * both want ten thousand strings, and the full recipient rows would be
 * megabytes to answer a question about fifteen bytes each.
 *
 * Paged, because PostgREST caps a response and a silently short list would mean
 * a clean-looking check that never saw the second half of the campaign. The cap
 * is a stop, not a trim — the caller is told when it was hit.
 */
export async function campaignPhones(
  campaignId: number,
  cap = 10_000,
): Promise<{ readonly phones: readonly string[]; readonly complete: boolean }> {
  const page = 1_000;
  const phones: string[] = [];

  for (let offset = 0; offset < cap; offset += page) {
    const rows = await read<readonly { readonly phone: string }[]>(
      `/recipients?campaign_id=eq.${campaignId}&select=phone&order=line&offset=${offset}&limit=${page}`,
    );
    phones.push(...rows.map((row) => row.phone));

    if (rows.length < page) return { phones, complete: true };
  }

  return { phones, complete: false };
}
